import { LogUserActivity } from '@/apis/users/requests'
import AppAlertComponent from '@/app/common/components/AppAlertComponent/AppAlertComponent'
import { buttonVariants } from '@/app/common/components/Button'
import Drawer from '@/app/common/components/Drawer'
import { ClearMeIcon } from '@/app/common/icons/general'
import { LOCAL_STORAGE_KEYS } from '@/app/common/local-storage'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import {
  BankIcon,
  LightningIcon,
  LockIcon,
  MobileIcon,
  ShieldIcon,
} from '@/common/icons/general.tsx'
import useIdentityVerification from '@/queries/identity_verification/useIdentityVerification'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { selectFamilyApplication } from '@/storage/applications/familyApplicationSlice'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { getPrivacyPolicyUrl, LOG_TYPES } from '../constants'
import useFamilyApplicationRouting from '../routing/useFamilyApplicationRouting'
import { SubmitButton } from '../student_details/shared/submit-button'
import {
  didNotCompleteIdentityVerification,
  getStudentDetailsStartRoute,
} from '../utils'

export default function IdentityVerificationPage() {
  const { t: translate, i18n } = useTranslation()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { initiateIdentityVerification } = useIdentityVerification()
  const token = localStorage.getItem(LOCAL_STORAGE_KEYS.clearme_id)
  const familyApplication = useSelector(selectFamilyApplication)
  const { onModuleEnd } = useFamilyApplicationRouting(familyApplication)

  const pdfUrl =
    'https://dev-ace-api.tabpoint.us/Files/ACE_Privacy_Policy_2024-12-13.pdf'

  function handleVerifyIdentityClick() {
    initiateIdentityVerification.mutate()
  }

  const hasLoggedPageView = useRef(false);
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Identity verification page',
      })
      hasLoggedPageView.current = true;
    }
  }, []) 

  const openPdfInNewTab = (): void => {
    if (!pdfUrl) {
      return
    }
    LogUserActivity({
      log_type: LOG_TYPES.VIEW_PAGE,
      suffix_text: `Privacy Policy Document.`,
    })
    window.open(getPrivacyPolicyUrl(i18n.language), '_blank')
  }

  if (!didNotCompleteIdentityVerification(familyApplication.flags) && !token) {
    const startRoute = getStudentDetailsStartRoute(familyApplication)
    const nextRoute = onModuleEnd(startRoute)
    return (
      <div className="flex items-center justify-center h-full">
        <AppAlertComponent
          variant="success"
          title={translate('identity_verification_complete')}
          text={`${translate('identity_verification_is_already_complete_for')} ${familyApplication.first_name} ${familyApplication.last_name}.`}
        >
          <Link
            to={nextRoute}
            className={buttonVariants({
              variant: 'primary',
            })}
          >
            {translate('continue')}
          </Link>
        </AppAlertComponent>
      </div>
    )
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('identity_verification')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content>
        <div className="flex items-center justify-center mb-10">
          {/* Icons representing steps */}
          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
            <MobileIcon className="text-xl" />
          </div>
          <div className="mx-2">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <ShieldIcon className="text-3xl" />
            </div>
          </div>
          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
            <BankIcon className="text-xl" />
          </div>
        </div>
        <h2 className="text-xl font-medium text-gray-900 mb-10 sm:mb-5">
          {translate('verify_your_identity')}
        </h2>

        {/* Description */}
        <div>
          <div className="flex items-center">
            <LightningIcon />
            <div className="ml-2 font-medium">
              {translate('what_to_expect')}
            </div>
          </div>
          <div className="ml-6 mt-1 text-gray-700 text-sm">
            {translate('verification_usually_takes')}.
          </div>
          <div className="ml-6 mt-1 text-gray-700 text-sm">
            {translate('to_apply_for_utah')}.
          </div>
        </div>
        <div className="mt-6">
          <div className="flex items-center">
            <LockIcon />
            <div className="ml-2 font-medium">{translate('what_you_need')}</div>
          </div>
          <div className="ml-6 mt-1 text-gray-700 text-sm">
            {translate('proof_of_identity')}.
          </div>
          <div className="ml-6 mt-1 text-gray-700 text-sm">
            {translate('prof_of_residency')}.
          </div>
          <div className="ml-6 mt-1 text-gray-700 text-sm">
            {translate('to_learn_how')}{' '}
            <span
              onClick={() => openPdfInNewTab()}
              className="text-primary-500 underline cursor-pointer"
              title="Haz clic para abrir el PDF"
            >
              {translate('privacy_statement')}
            </span>
          </div>
        </div>

        <Drawer open={drawerOpen} setOpen={setDrawerOpen}>
          <div className="text-xl font-medium text-gray-900 py-10 text-center px-5">
            {translate('privacy_statement')}
          </div>
        </Drawer>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <SubmitButton
          buttonTxt={translate('register_with')}
          className="py-1"
          submittingText={`${translate('starting_verification')}...`}
          onClick={handleVerifyIdentityClick}
          status={initiateIdentityVerification.status}
        >
           <ClearMeIcon className="ml-2 h-8" />
        </SubmitButton>
      </FamilyWizardLayout.ActionBar>
      <FamilyWizardLayout.ActionBar className="mt-2 justify-center">
        <Link
          className="text-sm font-medium link text-primary cursor-pointer underline"
          to={
            AppRoutePaths.family_application.identity_verification
              .verify_information
          }
        >
          {translate('register_without_clear')}
        </Link>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
